"use strict";
/**
 * Technical primitives for runtime configuration.
 *
 * The module is environment-agnostic on purpose: it never touches `process` or
 * `import.meta`, so the same parsing rules apply to a Node process and to a
 * bundled browser application. Each consumer passes its own raw source.
 *
 * Business configuration, feature flags and secrets are out of scope.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigurationError = exports.APP_ENVIRONMENTS = void 0;
exports.readString = readString;
exports.readInteger = readInteger;
exports.readEnum = readEnum;
exports.isAppEnvironment = isAppEnvironment;
exports.readAppEnvironment = readAppEnvironment;
/**
 * Deployment environments approved by the Architecture Baseline (DEV → TEST → PROD).
 */
exports.APP_ENVIRONMENTS = ['DEV', 'TEST', 'PROD'];
/** Raised when a configuration value is missing or technically invalid. */
class ConfigurationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ConfigurationError';
    }
}
exports.ConfigurationError = ConfigurationError;
function readRaw(source, key) {
    const raw = source[key];
    if (raw === undefined) {
        return undefined;
    }
    const trimmed = raw.trim();
    return trimmed.length === 0 ? undefined : trimmed;
}
function readString(source, key, fallback) {
    const raw = readRaw(source, key);
    if (raw !== undefined) {
        return raw;
    }
    if (fallback !== undefined) {
        return fallback;
    }
    throw new ConfigurationError(`Missing required configuration value: ${key}`);
}
function readInteger(source, key, options = {}) {
    const raw = readRaw(source, key);
    if (raw === undefined) {
        if (options.fallback === undefined) {
            throw new ConfigurationError(`Missing required configuration value: ${key}`);
        }
        return options.fallback;
    }
    if (!/^-?\d+$/.test(raw)) {
        throw new ConfigurationError(`Configuration value ${key} must be an integer, received: ${raw}`);
    }
    const value = Number.parseInt(raw, 10);
    if (options.min !== undefined && value < options.min) {
        throw new ConfigurationError(`Configuration value ${key} must be >= ${options.min}`);
    }
    if (options.max !== undefined && value > options.max) {
        throw new ConfigurationError(`Configuration value ${key} must be <= ${options.max}`);
    }
    return value;
}
/**
 * Reads a value restricted to a fixed set of allowed literals.
 *
 * `normalize` lets a caller accept case-insensitive input while still storing a
 * single canonical spelling.
 */
function readEnum(source, key, allowed, options = {}) {
    const raw = readRaw(source, key);
    if (raw === undefined) {
        if (options.fallback === undefined) {
            throw new ConfigurationError(`Missing required configuration value: ${key}`);
        }
        return options.fallback;
    }
    const normalized = options.normalize ? options.normalize(raw) : raw;
    const match = allowed.find((candidate) => candidate === normalized);
    if (match === undefined) {
        throw new ConfigurationError(`Configuration value ${key} must be one of ${allowed.join(' | ')}, received: ${raw}`);
    }
    return match;
}
function isAppEnvironment(value) {
    return exports.APP_ENVIRONMENTS.includes(value);
}
function readAppEnvironment(source, key, fallback = 'DEV') {
    return readEnum(source, key, exports.APP_ENVIRONMENTS, {
        fallback,
        normalize: (raw) => raw.toUpperCase(),
    });
}
//# sourceMappingURL=index.js.map